/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback } from 'react';
import { FlatList, Text, View } from 'react-native';
import type { BulkPreviewItem as InventoryBulkPreviewRow } from '../domain/catalogTypes';
import { formatQty } from '@shared/quantity';
import { formatMoney } from '../../shared/format/public-api';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { bulkCoefficientModalStyles as styles } from './bulkCoefficientModal.styles';
import {
  BULK_COEFFICIENT_PREVIEW_FETCH_LIMIT,
  type BulkApplyTo,
} from './useBulkCoefficientModal';

type BulkCoefficientPreviewProps = {
  narrow: boolean;
  bulkApplyTo: BulkApplyTo;
  previewRows: InventoryBulkPreviewRow[];
  previewCount: number;
  percentLabel: string;
  scopeLabel: string;
  /** Ítems del alcance sin precio cargado: quedan fuera aunque apliquen al filtro. */
  unpricedScopeCount?: number;
};

const formatPreviewValue = (item: InventoryBulkPreviewRow, value: number, bulkApplyTo: BulkApplyTo) =>
  bulkApplyTo === 'prices' ? formatMoney(value) : formatQty(value, item.unitOfMeasure);

export const BulkCoefficientPreview = ({
  narrow,
  bulkApplyTo,
  previewRows,
  previewCount,
  percentLabel,
  scopeLabel,
  unpricedScopeCount = 0,
}: BulkCoefficientPreviewProps) => {
  const hitFetchCap = previewCount >= BULK_COEFFICIENT_PREVIEW_FETCH_LIMIT;

  const renderItem = useCallback(
    ({ item }: { item: InventoryBulkPreviewRow }) =>
      narrow ? (
        <View style={styles.previewRow}>
          <Text style={styles.previewName} numberOfLines={2}>
            {item.name}
          </Text>
          <View style={styles.previewNarrowVals}>
            <Text style={styles.previewOld}>{formatPreviewValue(item, item.currentValue, bulkApplyTo)}</Text>
            <Text style={styles.previewArrow}>→</Text>
            <Text style={styles.previewNew}>{formatPreviewValue(item, item.newValue, bulkApplyTo)}</Text>
          </View>
        </View>
      ) : (
        <View style={styles.previewRowWide}>
          <Text style={styles.previewNameWide} numberOfLines={2}>
            {item.name}
          </Text>
          <Text style={styles.previewOldWide}>{formatPreviewValue(item, item.currentValue, bulkApplyTo)}</Text>
          <Text style={styles.previewNewWide}>{formatPreviewValue(item, item.newValue, bulkApplyTo)}</Text>
        </View>
      ),
    [bulkApplyTo, narrow]
  );

  const listHeader = (
    <View style={styles.previewSummary}>
      <Text style={styles.previewSummaryKicker}>Vista previa lista</Text>
      <Text style={styles.previewSummaryMeta}>
        {previewCount} ítem{previewCount === 1 ? '' : 's'} · {percentLabel} · {scopeLabel}
      </Text>
      {bulkApplyTo === 'prices' && unpricedScopeCount > 0 ? (
        <Text style={styles.previewSummaryWarn}>
          {unpricedScopeCount} producto{unpricedScopeCount === 1 ? '' : 's'} sin precio en este
          alcance: no se tocan.
        </Text>
      ) : null}
      {hitFetchCap ? (
        <Text style={styles.previewSummaryWarn}>
          Tope de {BULK_COEFFICIENT_PREVIEW_FETCH_LIMIT} por corrida. Acotá por categoría o búsqueda
          si necesitás el resto.
        </Text>
      ) : null}
      {!narrow ? (
        <View style={styles.previewTableHead}>
          <Text style={styles.previewThProduct}>Producto</Text>
          <Text style={styles.previewThNum}>Actual</Text>
          <Text style={styles.previewThNum}>Resultado</Text>
        </View>
      ) : null}
    </View>
  );

  return (
    <FlatList
      data={previewRows}
      keyExtractor={(item) => item.id}
      style={styles.previewList}
      contentContainerStyle={styles.previewListContent}
      renderItem={renderItem}
      ListHeaderComponent={listHeader}
      initialNumToRender={24}
      maxToRenderPerBatch={32}
      windowSize={7}
      removeClippedSubviews
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator
      ListEmptyComponent={
        <Text style={{ color: ticketColors.ink3, fontFamily: ticketFonts.sans, padding: 16 }}>
          Sin productos en este alcance.
        </Text>
      }
    />
  );
};
